import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { lowerCase, startCase } from 'lodash-es';
import {
  VaRadio,
  VaRadioOption,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import api from '../../../utilities/api';
import { LINK_ERROR_MESSAGES } from './linkErrorMessages';

/* eslint-disable camelcase */
const INDIVIDUAL_TYPE_LABELS = {
  attorney: 'Attorney',
  claims_agent: 'Claims agent',
  representative: 'VSO representative',
};
/* eslint-enable camelcase */

const getIndividualTypeLabel = type =>
  INDIVIDUAL_TYPE_LABELS[type] || startCase(lowerCase(type.replace(/_/g, ' ')));

// TODO: confirm final copy with design/content; "no_registration_found" body is a best guess
// based on a partially-visible mock ("Make sure your number is correct…").
// Keys below are literal error-code strings the backend returns as array values (not JSON keys
// subject to camelCase inflection), so they must stay snake_case to match.
/* eslint-disable camelcase */
const REGISTRATION_ERROR_MESSAGES = {
  registration_number_not_found: {
    title: 'No registration found',
    body: 'Make sure your number is correct, or contact OGC if you believe this is a mistake.',
  },
  no_email_on_file: {
    title: 'We can’t send you a code',
    body: 'We don’t have an email on file for that registration number. Contact OGC to update your contact information.',
  },
  email_shared: {
    title: 'We can’t send you a code',
    body: 'The email on file for that registration number is shared with another record. Contact OGC to resolve this.',
  },
  already_linked: {
    title: 'This registration is already linked',
    body: 'That registration number is already linked to a different account. Contact OGC if you believe this is a mistake.',
  },
  ...LINK_ERROR_MESSAGES,
};
/* eslint-enable camelcase */

const DEFAULT_ERROR_MESSAGE = {
  title: 'Something went wrong',
  body: 'We couldn’t send your code. Try again, or contact OGC if the problem continues.',
};

const validateRegistrationNumber = value => {
  if (!value || !value.trim()) {
    return 'Please enter your registration number';
  }
  return null;
};

/**
 * First step of the PIV self-service claim flow: the user enters their OGC registration number.
 * On success, the backend has sent a one-time code to the email on file, and we move to the code
 * entry step. When the number matches more than one individual type (for example an attorney who
 * is also a VSO representative), the user is asked to pick which type they mean.
 */
const RegistrationNumberStep = ({ onCodeSent, onLinked }) => {
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [individualType, setIndividualType] = useState('');
  const [candidateTypes, setCandidateTypes] = useState(null);
  const [fieldError, setFieldError] = useState('');
  const [apiError, setApiError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const validationError = validateRegistrationNumber(registrationNumber);
    if (validationError) {
      setFieldError(validationError);
      return;
    }
    setFieldError('');
    setApiError(null);
    setIsSubmitting(true);

    try {
      const response = await api.requestPivClaimCode(
        registrationNumber.trim(),
        individualType || undefined,
      );
      const body = await response.json();
      // The backend decides whether auto-link runs; we only branch on the response shape.
      if (body.linked) {
        onLinked();
        return;
      }
      onCodeSent({
        registrationNumber: registrationNumber.trim(),
        individualType,
        maskedEmail: body.maskedEmail,
      });
    } catch (err) {
      if (err instanceof Response) {
        if (err.status === 429) {
          setApiError({
            title: 'Too many attempts',
            body: 'You’ve requested too many codes. Wait a while before trying again.',
          });
        } else {
          const body = await err.json().catch(() => ({}));
          const [errorCode] = body?.errors || [];
          if (errorCode === 'multiple_individual_types') {
            setCandidateTypes(body.candidateTypes || []);
          } else {
            setApiError(
              REGISTRATION_ERROR_MESSAGES[errorCode] || DEFAULT_ERROR_MESSAGE,
            );
          }
        }
      } else {
        setApiError(DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <h1 className="dashboard__header">Set up your PIV card</h1>
      <p>
        In order to use your PIV card to sign in to the Accredited
        Representative Portal, a one time action is needed to pair it with your
        accreditation.
      </p>
      <p>
        To complete the pairing, enter your registration number below to receive
        a 6-digit verification code at the email address associated with your
        accreditation. Then enter the code on the next screen.
      </p>
      <va-text-input
        class="vads-u-margin-top--3"
        label="Registration number"
        name="registrationNumber"
        value={registrationNumber}
        onInput={e => {
          setRegistrationNumber(e.target.value);
          // The picker and selection belong to the previous number.
          setCandidateTypes(null);
          setIndividualType('');
        }}
        required
        error={fieldError}
        data-testid="piv-claim-registration-number-input"
        show-input-error
      />
      {candidateTypes && (
        <VaRadio
          label="This registration number is shared by more than one accreditation type. Which one are you?"
          data-testid="piv-claim-individual-type-radio"
          onVaValueChange={e => setIndividualType(e.detail.value)}
        >
          {candidateTypes.map(type => (
            <VaRadioOption
              key={type}
              name="individualType"
              value={type}
              label={getIndividualTypeLabel(type)}
              checked={individualType === type}
            />
          ))}
        </VaRadio>
      )}
      <div className="vads-u-display--flex vads-u-margin-top--4">
        <va-button
          big
          onClick={handleSubmit}
          text="Continue"
          data-testid="piv-claim-continue-button"
          uswds
          loading={isSubmitting}
        />
      </div>
      {apiError && (
        <va-alert
          status="error"
          visible
          class="vads-u-margin-top--3"
          data-testid="piv-claim-registration-error-alert"
        >
          <h2 slot="headline">{apiError.title}</h2>
          <p className="vads-u-margin-y--0">{apiError.body}</p>
        </va-alert>
      )}
    </>
  );
};

RegistrationNumberStep.propTypes = {
  onCodeSent: PropTypes.func.isRequired,
  onLinked: PropTypes.func.isRequired,
};

export default RegistrationNumberStep;
